import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Trash2, Play, ChevronDown, ChevronRight, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { useWorkoutPlan, useWorkoutPlans, useExercisesByIds } from '@/hooks/useWorkout';
import { useTelegram } from '@/hooks/useTelegram';
import { LEVEL_META, EMPHASIS_META, getCyclePhase } from '@/types/workout';
import { supabase } from '@/lib/supabase';
import type { Exercise } from '@/types/workout';

export default function WorkoutPlanDetailScreen() {
  const { planId } = useParams<{ planId: string }>();
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { plan, loading, updatePlanData } = useWorkoutPlan(planId ?? null);
  const { deletePlan } = useWorkoutPlans();
  const [expandedSession, setExpandedSession] = useState<number | null>(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Замена упражнения
  const [replacing, setReplacing] = useState<{ sessionIdx: number; exIdx: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Exercise[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const allExerciseIds = plan?.plan_data?.sessions.flatMap((s) =>
    s.exercises.map((e) => e.exercise_id)
  ) ?? [];
  const { exercises: exerciseMap } = useExercisesByIds(allExerciseIds);

  async function handleDelete() {
    if (!plan) return;
    haptic('warning' as any);
    await deletePlan(plan.id);
    nav('/chat?tab=workout');
  }

  function openReplace(sessionIdx: number, exIdx: number) {
    haptic('light');
    setReplacing({ sessionIdx, exIdx });
    setSearchQuery('');
    setSearchResults([]);
  }

  function closeReplace() {
    setReplacing(null);
    setSearchQuery('');
    setSearchResults([]);
  }

  function handleSearchChange(q: string) {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    searchTimer.current = setTimeout(async () => {
      const { data } = await supabase
        .from('exercises')
        .select('id, name, gif_url, target, equipment, muscle_group, category, secondary_muscles, instructions_en, instructions_ru, image_url')
        .ilike('name', `%${q}%`)
        .limit(30);
      setSearchResults((data ?? []) as Exercise[]);
      setSearchLoading(false);
    }, 400);
  }

  async function confirmReplace(newEx: Exercise) {
    if (!replacing || !plan?.plan_data) return;
    setSaving(true);
    haptic('medium');

    const { sessionIdx, exIdx } = replacing;
    const newSessions = plan.plan_data.sessions.map((s, si) =>
      si === sessionIdx
        ? {
            ...s,
            exercises: s.exercises.map((ex, ei) =>
              ei === exIdx ? { ...ex, exercise_id: newEx.id } : ex
            ),
          }
        : s
    );

    await updatePlanData({ ...plan.plan_data, sessions: newSessions });
    setSaving(false);
    haptic('success');
    closeReplace();
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-glow" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-4xl mb-3">🤔</div>
          <div className="font-semibold">План не найден</div>
          <button onClick={() => nav('/chat?tab=workout')} className="mt-4 text-emerald-glow text-sm">← Назад</button>
        </div>
      </div>
    );
  }

  const phase = getCyclePhase(plan.current_week);
  const cyclePos = ((plan.current_week - 1) % 4) + 1;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Хедер */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button onClick={() => nav('/chat?tab=workout')} className="p-2 -ml-2">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{plan.name}</div>
          <div className={`flex items-center gap-1.5 text-[10px] ${phase.color} mt-0.5`}>
            <span>{phase.emoji}</span>
            <span>{phase.label}</span>
            <span className="text-ink-dim">· нед {plan.current_week}</span>
            <div className="flex gap-0.5 ml-1">
              {[1,2,3,4].map(i => (
                <div key={i} className={`w-3 h-1 rounded-full ${i <= cyclePos ? 'bg-emerald-glow' : 'bg-bg-elevated'}`} />
              ))}
            </div>
          </div>
        </div>
        <button
          onClick={() => setConfirmDelete(true)}
          className="p-2 text-red-400 rounded-xl border border-line active:bg-red-500/10"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-4">
        {/* Метаданные */}
        <div className="bg-bg-card border border-line rounded-2xl p-3 flex flex-wrap gap-2">
          {[
            { label: LEVEL_META[plan.level].label, emoji: '🎯' },
            { label: `${plan.days_per_week}×/нед`, emoji: '📅' },
            { label: EMPHASIS_META[plan.emphasis].label, emoji: '💪' },
            { label: phase.desc, emoji: phase.emoji },
          ].map(({ label, emoji }) => (
            <span key={label} className="flex items-center gap-1 text-[11px] text-ink-muted bg-bg-elevated px-2 py-1 rounded-full">
              <span>{emoji}</span> {label}
            </span>
          ))}
        </div>

        {/* Методические заметки */}
        {plan.plan_data?.methodology_notes && (
          <div className="bg-bg-card border border-line rounded-xl p-3">
            <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold mb-1">Методика</div>
            <p className="text-[11px] text-ink-muted leading-relaxed">{plan.plan_data.methodology_notes}</p>
          </div>
        )}

        {/* Сессии */}
        <div className="space-y-3">
          <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold px-1">
            Тренировки плана
          </div>

          {plan.plan_data?.sessions.map((session, si) => (
            <motion.div
              key={si}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: si * 0.07 }}
              className="bg-bg-card border border-line rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => { haptic('light'); setExpandedSession(expandedSession === si ? null : si); }}
                className="w-full flex items-center gap-3 p-4 active:bg-bg-elevated transition-colors"
              >
                <div className="w-9 h-9 rounded-xl bg-emerald-glow/10 flex items-center justify-center shrink-0">
                  <span className="font-bold text-sm text-emerald-glow">{si + 1}</span>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold">{session.name}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5">
                    {session.exercises.length} упражнений
                    {(session as any).scheduled_day && (
                      <span className="ml-2 text-emerald-glow/70">
                        · {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'][(session as any).scheduled_day - 1]}
                      </span>
                    )}
                  </div>
                </div>
                {expandedSession === si
                  ? <ChevronDown size={16} className="text-ink-dim shrink-0" />
                  : <ChevronRight size={16} className="text-ink-dim shrink-0" />
                }
              </button>

              {expandedSession === si && (
                <div className="border-t border-line px-3 py-2 space-y-2">
                  {session.warmup_notes && (
                    <div className="text-[10px] text-orange-400/80 bg-orange-500/5 rounded-lg px-2 py-1.5">
                      🔥 Разминка: {session.warmup_notes}
                    </div>
                  )}

                  {session.exercises.map((ex, ei) => {
                    const exInfo = exerciseMap[ex.exercise_id];
                    return (
                      <div key={ei} className="flex items-center gap-2.5 py-1">
                        {exInfo?.gif_url ? (
                          <img src={exInfo.gif_url} alt={exInfo.name} className="w-9 h-9 rounded-lg object-cover bg-bg-elevated shrink-0" loading="lazy" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-bg-elevated flex items-center justify-center shrink-0">
                            <span className="text-sm">💪</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-medium truncate">{exInfo?.name ?? ex.exercise_id}</div>
                          <div className="text-[10px] text-ink-dim">{ex.sets} × {ex.reps} · {ex.rest_seconds}с отдых</div>
                        </div>
                        {/* Кнопка замены */}
                        <button
                          onClick={() => openReplace(si, ei)}
                          className="p-1.5 rounded-lg bg-bg-elevated text-ink-dim active:text-emerald-glow active:bg-emerald-glow/10 transition-colors shrink-0"
                          title="Заменить упражнение"
                        >
                          <RefreshCw size={13} />
                        </button>
                      </div>
                    );
                  })}

                  {session.cooldown_notes && (
                    <div className="text-[10px] text-sky-400/80 bg-sky-500/5 rounded-lg px-2 py-1.5">
                      🧘 Заминка: {session.cooldown_notes}
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-line p-3">
                <button
                  onClick={() => { haptic('medium'); nav(`/workout/session/${plan.id}/${si}`); }}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-glow/15 text-emerald-glow font-semibold py-2.5 rounded-xl active:scale-[0.98] transition-all text-sm"
                >
                  <Play size={14} className="fill-current" />
                  Начать тренировку
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {plan.plan_data?.disclaimer && (
          <div className="bg-bg-elevated border border-line rounded-xl p-3">
            <p className="text-[10px] text-ink-dim leading-relaxed">⚠️ {plan.plan_data.disclaimer}</p>
          </div>
        )}
      </div>

      {/* Диалог удаления */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(false)} />
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }}
            className="relative w-full bg-bg border-t border-line rounded-t-2xl p-6 space-y-3"
          >
            <div className="font-semibold text-base text-center">Удалить план?</div>
            <div className="text-[12px] text-ink-muted text-center">История тренировок сохранится</div>
            <button onClick={handleDelete} className="w-full py-3 rounded-2xl bg-red-500/15 text-red-400 font-semibold active:scale-[0.98]">
              Удалить
            </button>
            <button onClick={() => setConfirmDelete(false)} className="w-full py-3 rounded-2xl border border-line font-medium text-sm active:scale-[0.98]">
              Отмена
            </button>
          </motion.div>
        </div>
      )}

      {/* Оверлей замены упражнения */}
      <AnimatePresence>
        {replacing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-bg"
          >
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-line">
              <button onClick={closeReplace} className="p-2 -ml-2">
                <X size={20} />
              </button>
              <div className="flex-1">
                <div className="font-semibold text-sm">Заменить упражнение</div>
                <div className="text-[10px] text-ink-dim">
                  {(() => {
                    const ex = plan?.plan_data?.sessions[replacing.sessionIdx]?.exercises[replacing.exIdx];
                    const info = ex ? exerciseMap[ex.exercise_id] : null;
                    return info ? `Заменяем: ${info.name}` : '';
                  })()}
                </div>
              </div>
              {saving && <Loader2 size={16} className="animate-spin text-emerald-glow" />}
            </div>

            <div className="px-4 pt-3 pb-2 border-b border-line">
              <div className="flex items-center gap-2 bg-bg-elevated border border-line rounded-xl px-3 py-2">
                <Search size={14} className="text-ink-dim shrink-0" />
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder="Найти замену..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-dim"
                />
                {searchLoading && <Loader2 size={13} className="animate-spin text-ink-dim shrink-0" />}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {!searchQuery && (
                <div className="text-center py-12 text-ink-dim text-sm">
                  Введи название упражнения для поиска замены
                </div>
              )}
              {searchQuery && !searchLoading && searchResults.length === 0 && (
                <div className="text-center py-12 text-ink-dim text-sm">Ничего не найдено</div>
              )}
              {searchResults.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => confirmReplace(ex)}
                  disabled={saving}
                  className="w-full flex items-center gap-3 bg-bg-card border border-line rounded-xl p-3 active:bg-bg-elevated transition-colors disabled:opacity-50"
                >
                  {ex.gif_url ? (
                    <img src={ex.gif_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 bg-bg-elevated" loading="lazy" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-bg-elevated flex items-center justify-center shrink-0 text-lg">💪</div>
                  )}
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium truncate">{ex.name}</div>
                    <div className="text-[11px] text-ink-dim truncate">{ex.target ?? ex.category} · {ex.equipment}</div>
                  </div>
                  <RefreshCw size={15} className="text-emerald-glow shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
